import { HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { HANDLED_ERRORS } from '../../../../shared/configs/http-context.config';
import { MasterListType } from '../../../../shared/configs/master-list-type.config';
import { UserTypes } from '../../../../shared/configs/user-types.config';
import { ApiResponse } from '../../../../shared/models/api-response';
import { Batch, BatchSubject } from '../../../../shared/models/institutions';
import { MasterListItem, MasterListsObj } from '../../../../shared/models/master-list';
import { UserType } from '../../../../shared/models/user-type.model';
import { ApiService } from '../api.service';

import { Lesson } from 'projects/admin/src/app/shared/models/content';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CommonService extends ApiService {
  assignMailboxCentres(
    formData: any,
    handledErrors: number[] = [],
  ): Observable<ApiResponse<MasterListItem[]>> {
    const context = new HttpContext().set(HANDLED_ERRORS, handledErrors);
    const headers = new HttpHeaders();

    return this.post<ApiResponse<MasterListItem[]>>(
      `${this.apiUrl.commonService}/public/api/v1/mailbox-to-centre`,
      formData,
      {
        context,
        headers,
      },
    );
  }

  assignSubjectsToBatch(
    batchId: string,
    subjectList: string[],
    handledErrors: number[] = [],
  ): Observable<ApiResponse<BatchSubject>> {
    const context = new HttpContext().set(HANDLED_ERRORS, handledErrors);

    return this.post<ApiResponse<BatchSubject>>(
      `${this.apiUrl.commonService}/public/api/v1/batch/${batchId}/assign-subjects`,
      { subjects: subjectList },
      {
        context,
      },
    );
  }

  fetchAllDistricts(): Observable<ApiResponse<any[]>> {
    return this.get<ApiResponse<any[]>>(
      `${this.apiUrl.commonService}/public/api/v1/get-alldistrict`,
    );
  }

  fetchAllSubjectsUnderBatch(batchId: string): Observable<ApiResponse<BatchSubject[]>> {
    return this.get<ApiResponse<BatchSubject[]>>(
      `${this.apiUrl.commonService}/public/api/v1/batch/${batchId}/list-subjects`,
    );
  }

  fetchAllTradesOfCentre(centreId: string): Observable<ApiResponse<MasterListItem[]>> {
    return this.get<ApiResponse<MasterListItem[]>>(
      `${this.apiUrl.commonService}/public/api/v1/centre/${centreId}/list-trades`,
    );
  }

  fetchBatchesUnderCentre(centreId: string): Observable<ApiResponse<Batch[]>> {
    return this.get<ApiResponse<Batch[]>>(
      `${this.apiUrl.commonService}/public/api/v1/centre/${centreId}/batch`,
    );
  }

  fetchBatchesUnderCentres(centreIdList: string[]): Observable<ApiResponse<MasterListItem[]>> {
    return this.get<ApiResponse<MasterListItem[]>>(
      `${this.apiUrl.commonService}/public/api/v1/multiple-centre/batch`,
      {
        params: this.getQueryStringParameters({
          'centre_id[]': centreIdList,
        }),
      },
    );
  }

  fetchCentresUnderInstitutionTypeAndState(
    centreTypeId: string,
    stateId: string,
  ): Observable<ApiResponse<MasterListItem[]>> {
    return this.get<ApiResponse<MasterListItem[]>>(
      `${this.apiUrl.commonService}/public/api/v1/centre-type/state/centre`,
      {
        params: this.getQueryStringParameters({
          centre_type_id: centreTypeId,
          state_id: stateId,
        }),
      },
    );
  }

  fetchCentresUnderMultipleOrganisation(
    organisationIdList: string[],
  ): Observable<ApiResponse<MasterListItem[]>> {
    return this.get<ApiResponse<MasterListItem[]>>(
      `${this.apiUrl.commonService}/public/api/v1/multiple-organisation/centre`,
      {
        params: this.getQueryStringParameters({
          'organisation_id[]': organisationIdList,
        }),
      },
    );
  }

  fetchCentresUnderOrganisation(organisationId: string): Observable<ApiResponse<MasterListItem[]>> {
    return this.get<ApiResponse<MasterListItem[]>>(
      `${this.apiUrl.commonService}/public/api/v1/organisation/${organisationId}/centre`,
    );
  }

  fetchCentresUnderState(stateId: string): Observable<ApiResponse<MasterListItem[]>> {
    return this.get<ApiResponse<MasterListItem[]>>(
      `${this.apiUrl.commonService}/public/api/v1/state/${stateId}/centre`,
    );
  }

  fetchCoursesUnderSubject(subjectId: string): Observable<ApiResponse<MasterListItem[]>> {
    return this.get<ApiResponse<MasterListItem[]>>(
      `${this.apiUrl.commonService}/public/api/v1/subject/${subjectId}/course`,
    );
  }

  fetchDistrictsUnderState(stateId: string): Observable<ApiResponse<MasterListItem[]>> {
    return this.get<ApiResponse<MasterListItem[]>>(
      `${this.apiUrl.commonService}/public/api/v1/state/${stateId}/district`,
    );
  }

  fetchMailboxCentres(formData: any): Observable<ApiResponse<MasterListItem[]>> {
    return this.get<ApiResponse<MasterListItem[]>>(
      `${this.apiUrl.commonService}/public/api/v1/list-centre`,
      { params: this.getQueryStringParameters(formData) },
    );
  }

  fetchMailboxes(): Observable<ApiResponse<MasterListItem[]>> {
    return this.get<ApiResponse<MasterListItem[]>>(
      `${this.apiUrl.commonService}/public/api/v1/mailbox`,
    );
  }

  fetchMasterLists(masterListTypeList: MasterListType[]): Observable<MasterListsObj> {
    const masterListTypeParameterList: string[] = [];
    masterListTypeList.forEach((masterListType) => {
      masterListTypeParameterList.push(`&list_models[]=${masterListType}`);
    });

    return this.get<MasterListsObj>(
      `${
        this.apiUrl.commonService
      }/public/api/v1/get-master-list?${masterListTypeParameterList.join('&')}`,
    );
  }

  fetchOrganisationsUnderState(stateId: string): Observable<ApiResponse<MasterListItem[]>> {
    return this.get<ApiResponse<MasterListItem[]>>(
      `${this.apiUrl.commonService}/public/api/v1/state/${stateId}/organisation`,
    );
  }

  fetchPhasesUnderProject(
    projectId: string,
    centreId: string,
  ): Observable<ApiResponse<MasterListItem[]>> {
    return this.get<ApiResponse<MasterListItem[]>>(
      `${this.apiUrl.commonService}/public/api/v1/project/${projectId}/phases`,
      {
        params: this.getQueryStringParameters({
          centre_id: centreId,
        }),
      },
    );
  }

  fetchCentresUnderPrograms(programIdList: string[]): Observable<ApiResponse<MasterListItem[]>> {
    return this.get<ApiResponse<MasterListItem[]>>(
      `${this.apiUrl.adminService}/public/api/v1/programs/centres`,
      {
        params: this.getQueryStringParameters({
          'program_id[]': programIdList,
        }),
      },
    );
  }

  fetchProgramsUnderCentre(centreId: string): Observable<ApiResponse<MasterListItem[]>> {
    return this.get<ApiResponse<MasterListItem[]>>(
      `${this.apiUrl.commonService}/public/api/v1/centre/${centreId}/programs`,
    );
  }

  fetchProjectsUnderProgram(programId: string): Observable<ApiResponse<MasterListItem[]>> {
    return this.get<ApiResponse<MasterListItem[]>>(
      `${this.apiUrl.commonService}/public/api/v1/program/${programId}/project`,
    );
  }

  fetchCategoryUnderTenant(
    tenantId: string,
    language_id?: string,
    category_id?: string,
  ): Observable<MasterListItem[]> {
    const params = new URLSearchParams();

    if (language_id) params.set('language_id', language_id);

    if (category_id) params.set('category_id', category_id);

    const queryParams = params.toString();

    const url = `${this.apiUrl.commonService}/public/api/v1/tenant/${tenantId}
    /faq-category${queryParams ? `?${queryParams}` : ''}`;

    return this.get<MasterListItem[]>(url);
  }

  fetchEsModules(): Observable<MasterListItem[]> {
    return this.get<MasterListItem[]>(`${this.apiUrl.adminService}/public/api/v1/esmodules`);
  }

  fetchSubCategoryUnderCategory(
    categoryId: string,
    language_id?: string,
    subcategoryId?: string,
  ): Observable<MasterListItem[]> {
    const params = new URLSearchParams();

    if (language_id) params.set('language_id', language_id);

    if (subcategoryId) params.set('sub_category_id', subcategoryId);

    const queryParams = params.toString();

    const url = `${this.apiUrl.commonService}/public/api/v1/faq-category/${categoryId || ''}
    /faq-subcategory${queryParams ? `?${queryParams}` : ''}`;

    return this.get<MasterListItem[]>(url);
  }

  fetchUsersUnderType(userType: UserTypes): Observable<ApiResponse<UserType[]>> {
    return this.get<ApiResponse<UserType[]>>(
      `${this.apiUrl.commonService}/public/api/v1/user-type-list`,
      {
        params: this.getQueryStringParameters(
          this.convertToFilterParams({
            type: userType,
          }),
        ),
      },
    );
  }

  uploadFile(formData: FormData): Observable<Pick<ApiResponse, 'url' | 'r2_url'>> {
    const headers = new HttpHeaders();

    return this.post<Pick<ApiResponse, 'url' | 'r2_url'>>(
      `${this.apiUrl.commonService}/public/api/v1/upload-file`,
      formData,
      {
        headers,
      },
    );
  }

  updateFreezeDemographicOverride(id: string, status: number): Observable<ApiResponse<Batch>> {
    return this.put<ApiResponse<Batch>>(
      `${this.apiUrl.commonService}/public/api/v1/batch/${id}/freeze-demographic-override`,
      null,
      {
        params: this.getQueryStringParameters({
          freeze_demographic_override: status,
        }),
      },
    );
  }

  updateFreezePlacementOverride(id: string, status: number): Observable<ApiResponse<Batch>> {
    return this.put<ApiResponse<Batch>>(
      `${this.apiUrl.commonService}/public/api/v1/batch/${id}/freeze-placement-override`,
      null,
      {
        params: this.getQueryStringParameters({
          freeze_placement_override: status,
        }),
      },
    );
  }

  viewSample(fileType: number, centreTypeId?: string): Observable<Pick<ApiResponse, 'path'>> {
    return this.post<Pick<ApiResponse, 'path'>>(
      `${this.apiUrl.commonService}/public/api/v1/sample-file`,
      {
        file_type: fileType,
        centre_type_id: centreTypeId,
      },
    );
  }

  viewSampleBatch(fileType: number, centreId?: string): Observable<Pick<ApiResponse, 'path'>> {
    return this.post<Pick<ApiResponse, 'path'>>(
      `${this.apiUrl.commonService}/public/api/v1/sample-file`,
      {
        file_type: fileType,
        centre_id: centreId,
      },
    );
  }

  fetchLessonById(lessonId: string): Observable<ApiResponse<Lesson>> {
    return this.get<ApiResponse<Lesson>>(
      `${this.apiUrl.adminService}/public/api/v1/lesson/${lessonId}`,
    );
  }
}
